import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  Save,
  ChefHat,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MEAL_TYPES = [
  { value: 'breakfast', label: 'Raňajky' },
  { value: 'lunch', label: 'Obed' },
  { value: 'dinner', label: 'Večera' },
  { value: 'snack', label: 'Desiata' },
  { value: 'dessert', label: 'Dezert' },
];
const SEASONS = [
  { value: 'spring', label: 'Jar' },
  { value: 'summer', label: 'Leto' },
  { value: 'autumn', label: 'Jeseň' },
  { value: 'winter', label: 'Zima' },
];
const CATEGORIES = [
  { value: 'fit', label: 'Fit' },
  { value: 'vegetarian', label: 'Vegetariánske' },
  { value: 'traditional', label: 'Tradičné' },
  { value: 'quick', label: 'Rýchle' },
  { value: 'festive', label: 'Sviatočné' },
  { value: 'meal_prep', label: 'Meal prep' },
];
const DIFFICULTY = [
  { value: 'easy', label: 'Ľahké' },
  { value: 'medium', label: 'Stredné' },
  { value: 'hard', label: 'Náročné' },
];

const emptyRecipe = () => ({
  name: '',
  name_cz: '',
  description: '',
  description_cz: '',
  ingredients: [{ name: '', amount: '', unit: '' }],
  instructions: [''],
  prep_time: 0,
  cook_time: 0,
  servings: 4,
  image_url: '',
  video_url: '',
  meal_type: [],
  season: [],
  category: [],
  difficulty: 'easy',
  tips: '',
  magic_five_reason: '',
});

// Toggle-badge multi-select
const TagPicker = ({ options, selected, onToggle, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((opt) => {
      const active = selected.includes(opt.value);
      return (
        <button
          type="button"
          key={opt.value}
          onClick={() => onToggle(opt.value)}
          data-testid={`${testid}-${opt.value}`}
          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
            active
              ? 'bg-secondary text-white border-secondary'
              : 'bg-white text-foreground border-border hover:border-secondary'
          }`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

export default function AdminRecipesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recipes, setRecipes] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(null); // recipe being edited/created
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [genImg, setGenImg] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (user && user.membership_type !== 'admin' && user.membership_type !== 'vip') {
      navigate('/');
    }
  }, [user, navigate]);

  const loadRecipes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/recipes?search=${encodeURIComponent(search)}&limit=100`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      setRecipes(data.recipes || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Nepodarilo sa načítať recepty');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(loadRecipes, 300);
    return () => clearTimeout(t);
  }, [loadRecipes]);

  const openNew = () => {
    setForm(emptyRecipe());
    setEditingId(null);
  };

  const openEdit = (r) => {
    setForm({
      name: r.name || '',
      name_cz: r.name_cz || '',
      description: r.description || '',
      description_cz: r.description_cz || '',
      ingredients:
        r.ingredients && r.ingredients.length
          ? r.ingredients.map((i) => ({ name: i.name || '', amount: i.amount || '', unit: i.unit || '' }))
          : [{ name: '', amount: '', unit: '' }],
      instructions: r.instructions && r.instructions.length ? [...r.instructions] : [''],
      prep_time: r.prep_time || 0,
      cook_time: r.cook_time || 0,
      servings: r.servings || 4,
      image_url: r.image_url || '',
      video_url: r.video_url || '',
      meal_type: r.meal_type || [],
      season: r.season || [],
      category: r.category || [],
      difficulty: r.difficulty || 'easy',
      tips: r.tips || '',
      magic_five_reason: r.magic_five_reason || '',
    });
    setEditingId(r.recipe_id);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (field, value) =>
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(value)
        ? f[field].filter((x) => x !== value)
        : [...f[field], value],
    }));

  // ingredients helpers
  const setIngredient = (idx, key, val) =>
    setForm((f) => {
      const ing = [...f.ingredients];
      ing[idx] = { ...ing[idx], [key]: val };
      return { ...f, ingredients: ing };
    });
  const addIngredient = () =>
    setForm((f) => ({ ...f, ingredients: [...f.ingredients, { name: '', amount: '', unit: '' }] }));
  const removeIngredient = (idx) =>
    setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));

  // instruction helpers
  const setStep = (idx, val) =>
    setForm((f) => {
      const st = [...f.instructions];
      st[idx] = val;
      return { ...f, instructions: st };
    });
  const addStep = () => setForm((f) => ({ ...f, instructions: [...f.instructions, ''] }));
  const removeStep = (idx) =>
    setForm((f) => ({ ...f, instructions: f.instructions.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Zadajte názov receptu');
      return;
    }
    const payload = {
      ...form,
      prep_time: parseInt(form.prep_time) || 0,
      cook_time: parseInt(form.cook_time) || 0,
      servings: parseInt(form.servings) || 1,
      ingredients: form.ingredients.filter((i) => i.name.trim()),
      instructions: form.instructions.filter((s) => s.trim()),
      image_url: form.image_url.trim() || null,
      video_url: form.video_url.trim() || null,
      name_cz: form.name_cz.trim() || null,
      description_cz: form.description_cz.trim() || null,
      tips: form.tips.trim() || null,
      magic_five_reason: form.magic_five_reason.trim() || null,
    };
    setSaving(true);
    try {
      const url = editingId
        ? `${API_URL}/api/admin/recipes/${editingId}`
        : `${API_URL}/api/admin/recipes`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('save failed');
      toast.success(editingId ? 'Recept upravený' : 'Recept vytvorený');
      setForm(null);
      setEditingId(null);
      loadRecipes();
    } catch (e) {
      toast.error('Uloženie zlyhalo');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!form.name.trim()) {
      toast.error('Najprv zadajte názov receptu');
      return;
    }
    setGenImg(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/recipes/generate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: form.name, description: form.description }),
      });
      if (!res.ok) throw new Error('gen failed');
      const data = await res.json();
      setField('image_url', `${API_URL}${data.image_path}`);
      toast.success('Obrázok vygenerovaný ✨');
    } catch (e) {
      toast.error('Generovanie obrázku zlyhalo, skúste znova');
    } finally {
      setGenImg(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/recipes/${deleteTarget.recipe_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('delete failed');
      toast.success('Recept zmazaný');
      setDeleteTarget(null);
      loadRecipes();
    } catch (e) {
      toast.error('Zmazanie zlyhalo');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-recipes-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
            data-testid="recipes-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Späť do adminu
          </button>
          <h1 className="text-3xl font-heading font-bold text-heading flex items-center gap-2">
            <ChefHat className="w-7 h-7 text-secondary" /> Správa receptov
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{total} receptov v databáze</p>
        </div>
        <Button onClick={openNew} data-testid="new-recipe-btn" className="gap-2">
          <Plus className="w-4 h-4" /> Nový recept
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Hľadať recept podľa názvu..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="recipes-search-input"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-secondary" />
        </div>
      ) : recipes.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="recipes-empty">
          Žiadne recepty. Vytvorte prvý pomocou „Nový recept".
        </div>
      ) : (
        <div className="space-y-2" data-testid="recipes-list">
          {recipes.map((r) => (
            <div
              key={r.recipe_id}
              className="flex items-center gap-3 bg-white rounded-xl border border-border/50 p-3"
              data-testid={`recipe-row-${r.recipe_id}`}
            >
              <div className="w-14 h-14 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                {r.image_url ? (
                  <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <ChefHat className="w-6 h-6" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground truncate">{r.name}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(r.meal_type || []).slice(0, 3).map((m) => (
                    <Badge key={m} variant="secondary" className="text-xs">
                      {MEAL_TYPES.find((x) => x.value === m)?.label || m}
                    </Badge>
                  ))}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEdit(r)}
                data-testid={`edit-recipe-${r.recipe_id}`}
                className="gap-1"
              >
                <Pencil className="w-4 h-4" /> Upraviť
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteTarget(r)}
                data-testid={`delete-recipe-${r.recipe_id}`}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recipe-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Upraviť recept' : 'Nový recept'}</DialogTitle>
            <DialogDescription>
              Vyplňte polia receptu. Povinný je iba názov.
            </DialogDescription>
          </DialogHeader>

          {form && (
            <div className="space-y-5">
              {/* Names */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Názov (SK) *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    data-testid="recipe-name-input"
                  />
                </div>
                <div>
                  <Label>Názov (CZ)</Label>
                  <Input
                    value={form.name_cz}
                    onChange={(e) => setField('name_cz', e.target.value)}
                    data-testid="recipe-name-cz-input"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <Label>Popis</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  rows={2}
                  data-testid="recipe-description-input"
                />
              </div>

              {/* Times / servings / difficulty */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <Label>Príprava (min)</Label>
                  <Input
                    type="number"
                    value={form.prep_time}
                    onChange={(e) => setField('prep_time', e.target.value)}
                    data-testid="recipe-prep-input"
                  />
                </div>
                <div>
                  <Label>Varenie (min)</Label>
                  <Input
                    type="number"
                    value={form.cook_time}
                    onChange={(e) => setField('cook_time', e.target.value)}
                    data-testid="recipe-cook-input"
                  />
                </div>
                <div>
                  <Label>Porcie</Label>
                  <Input
                    type="number"
                    value={form.servings}
                    onChange={(e) => setField('servings', e.target.value)}
                    data-testid="recipe-servings-input"
                  />
                </div>
                <div>
                  <Label>Náročnosť</Label>
                  <Select value={form.difficulty} onValueChange={(v) => setField('difficulty', v)}>
                    <SelectTrigger data-testid="recipe-difficulty-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DIFFICULTY.map((d) => (
                        <SelectItem key={d.value} value={d.value}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Ingredients */}
              <div>
                <Label>Ingrediencie</Label>
                <div className="space-y-2 mt-1" data-testid="recipe-ingredients">
                  {form.ingredients.map((ing, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        placeholder="Názov"
                        value={ing.name}
                        onChange={(e) => setIngredient(idx, 'name', e.target.value)}
                        className="flex-1"
                        data-testid={`ingredient-name-${idx}`}
                      />
                      <Input
                        placeholder="Množstvo"
                        value={ing.amount}
                        onChange={(e) => setIngredient(idx, 'amount', e.target.value)}
                        className="w-24"
                        data-testid={`ingredient-amount-${idx}`}
                      />
                      <Input
                        placeholder="Jednotka"
                        value={ing.unit}
                        onChange={(e) => setIngredient(idx, 'unit', e.target.value)}
                        className="w-24"
                        data-testid={`ingredient-unit-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeIngredient(idx)}
                        className="text-destructive flex-shrink-0"
                        data-testid={`remove-ingredient-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addIngredient}
                  className="mt-2 gap-1"
                  data-testid="add-ingredient-btn"
                >
                  <Plus className="w-4 h-4" /> Pridať ingredienciu
                </Button>
              </div>

              {/* Instructions */}
              <div>
                <Label>Postup (kroky)</Label>
                <div className="space-y-2 mt-1" data-testid="recipe-instructions">
                  {form.instructions.map((step, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <span className="mt-2 text-sm text-muted-foreground w-5 flex-shrink-0">
                        {idx + 1}.
                      </span>
                      <Textarea
                        value={step}
                        onChange={(e) => setStep(idx, e.target.value)}
                        rows={2}
                        className="flex-1"
                        data-testid={`instruction-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeStep(idx)}
                        className="text-destructive flex-shrink-0"
                        data-testid={`remove-instruction-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addStep}
                  className="mt-2 gap-1"
                  data-testid="add-instruction-btn"
                >
                  <Plus className="w-4 h-4" /> Pridať krok
                </Button>
              </div>

              {/* Tags */}
              <div>
                <Label>Typ jedla</Label>
                <div className="mt-1">
                  <TagPicker
                    options={MEAL_TYPES}
                    selected={form.meal_type}
                    onToggle={(v) => toggleTag('meal_type', v)}
                    testid="mealtype-picker"
                  />
                </div>
              </div>
              <div>
                <Label>Sezóna</Label>
                <div className="mt-1">
                  <TagPicker
                    options={SEASONS}
                    selected={form.season}
                    onToggle={(v) => toggleTag('season', v)}
                    testid="season-picker"
                  />
                </div>
              </div>
              <div>
                <Label>Kategória</Label>
                <div className="mt-1">
                  <TagPicker
                    options={CATEGORIES}
                    selected={form.category}
                    onToggle={(v) => toggleTag('category', v)}
                    testid="category-picker"
                  />
                </div>
              </div>

              {/* Tip Lucie */}
              <div>
                <Label>Tip Lucie</Label>
                <Textarea
                  value={form.tips}
                  onChange={(e) => setField('tips', e.target.value)}
                  rows={2}
                  placeholder="Osobný tip od Lucie k tomuto receptu..."
                  data-testid="recipe-tips-input"
                />
              </div>

              {/* Magic five */}
              <div>
                <Label>Prečo sedí na Magickú päťku</Label>
                <Textarea
                  value={form.magic_five_reason}
                  onChange={(e) => setField('magic_five_reason', e.target.value)}
                  rows={2}
                  data-testid="recipe-magic-input"
                />
              </div>

              {/* Image URL */}
              <div>
                <div className="flex items-center justify-between gap-2">
                  <Label>URL obrázku</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateImage}
                    disabled={genImg}
                    className="gap-1.5 text-primary border-primary/40 hover:bg-primary/5"
                    data-testid="generate-image-btn"
                  >
                    {genImg ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    {genImg
                      ? 'Generujem...'
                      : form.image_url
                      ? 'Vygenerovať znova'
                      : 'Vygenerovať obrázok'}
                  </Button>
                </div>
                <Input
                  value={form.image_url}
                  onChange={(e) => setField('image_url', e.target.value)}
                  placeholder="https://... alebo vygenerujte AI obrázok"
                  data-testid="recipe-image-input"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Vložte vlastnú URL, alebo nechajte AI vygenerovať obrázok podľa názvu a popisu.
                </p>
                {form.image_url ? (
                  <img
                    src={form.image_url}
                    alt="náhľad"
                    className="mt-2 w-32 h-32 object-cover rounded-lg border border-border"
                  />
                ) : null}
              </div>

              {/* Video URL */}
              <div>
                <Label>YouTube video (odkaz)</Label>
                <Input
                  value={form.video_url}
                  onChange={(e) => setField('video_url', e.target.value)}
                  placeholder="https://youtu.be/... alebo https://www.youtube.com/watch?v=..."
                  data-testid="recipe-video-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Funguje aj s „unlisted" videami. Zobrazí sa v detaile receptu ako prehrávateľné video.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-background">
                <Button variant="outline" onClick={() => setForm(null)} data-testid="recipe-cancel-btn">
                  Zrušiť
                </Button>
                <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="recipe-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingId ? 'Uložiť zmeny' : 'Vytvoriť recept'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-recipe-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Zmazať recept?</AlertDialogTitle>
            <AlertDialogDescription>
              Naozaj chcete zmazať „{deleteTarget?.name}"? Túto akciu nie je možné vrátiť.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Zrušiť</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="delete-confirm-btn"
            >
              Zmazať
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
